<template>
  <div class="onboarding-step" :class="{ 'completed': completed }">
    <div class="d-flex align-start">
      <v-icon :color="completed ? 'success' : 'grey lighten-1'" class="mr-2 mt-1" size="20">
        {{ completed ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}
      </v-icon>
      <div class="flex-grow-1">
        <div class="font-weight-medium step-title" :class="{ 'grey--text text--lighten-1': completed }">
          {{ step.title }}
        </div>
        <div v-if="step.description" class="text-caption grey--text step-description">
          {{ step.description }}
        </div>
        <!-- Mensagem para steps que requerem projeto -->
        <div v-if="showProjectRequiredMessage" class="text-caption grey--text text--darken-1 mt-1">
          <v-icon x-small class="mr-1">mdi-folder-open</v-icon>
          Abra um projeto para continuar
        </div>
        <div v-if="step.tutorialUrl" class="mt-1">
          <a :href="step.tutorialUrl" target="_blank" rel="noopener noreferrer" class="text-caption tutorial-link primary--text">
            <v-icon x-small color="primary" class="mr-1">mdi-video</v-icon>
            Assistir tutorial
          </a>
        </div>
      </div>
      <!-- Botao de navegacao (quando tem rota e nao requer projeto OU ja esta em projeto) -->
      <v-btn v-if="showNavigateButton"
             icon
             small
             color="primary"
             class="ml-2"
             @click="goToStep">
        <v-icon small>mdi-arrow-right</v-icon>
      </v-btn>
      <v-icon v-else-if="completed" small color="success" class="ml-2">
        mdi-check
      </v-icon>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, getCurrentInstance, PropType } from 'vue'
import { OnboardingStep } from '@/hooks/useOnboarding'

export default defineComponent({
  name: 'OnboardingStep',
  props: {
    step: {
      type: Object as PropType<OnboardingStep>,
      required: true
    },
    completed: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const vm = getCurrentInstance()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const root = vm?.proxy as any

    // Verifica se esta em uma rota de projeto
    const isInProjectRoute = computed(() => {
      const path = root?.$route?.path || ''
      return path.startsWith('/project/')
    })

    // Mostra mensagem de "abra um projeto" quando:
    // - Step requer projeto (isProjectRoute = true)
    // - Usuario NAO esta em rota de projeto
    // - Step NAO esta completo
    const showProjectRequiredMessage = computed(() => {
      return props.step.isProjectRoute && !isInProjectRoute.value && !props.completed
    })

    // Mostra botao de navegacao quando:
    // - Step NAO esta completo
    // - Step tem rota definida OU (isProjectRoute e esta em projeto)
    const showNavigateButton = computed(() => {
      if (props.completed) return false

      // Se tem rota estatica, mostra botao
      if (props.step.route) return true

      // Se requer projeto e esta em projeto, mostra botao (para acao no projeto atual)
      // Neste caso, a acao sera contextual ao projeto
      return false
    })

    const goToStep = () => {
      if (!props.step.route) return

      // Navegar diretamente para o path
      root.$router.push(props.step.route)
    }

    return {
      isInProjectRoute,
      showProjectRequiredMessage,
      showNavigateButton,
      goToStep
    }
  }
})
</script>

<style lang="scss" scoped>
.onboarding-step {
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &.completed {
    opacity: 0.7;

    .step-title {
      text-decoration: line-through;
    }
  }
}

.step-title {
  font-size: 14px;
  line-height: 1.4;
}

.step-description {
  line-height: 1.3;
  margin-top: 2px;
}

.tutorial-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }
}
</style>
